import React, { ReactNode, FC, useContext, InputHTMLAttributes } from 'react';

import { ReactComponent as CheckIcon } from '../../../icons/doneThick.svg';
import { StyledLabel, StyledToggleButton, StyledHiddenRadioButton, StyledBullet, StyledCheckIcon } from './ToggleButton.styles';
import FormColContext from '../_FormColContext';

export interface ToggleButtonProps extends InputHTMLAttributes<HTMLInputElement> {
  /** The content of the button. Alternative to using the `label` prop. */
  children?: ReactNode;
  /** The name of the radio item. */
  name?: string;
  /** The label to display inside the button. Alternative to using the button children. */
  label?: ReactNode;
  /** @ignore */
  isSwitch?: boolean;
  /** @ignore */
  isChips?: boolean;
  /** @ignore */
  disabled?: boolean;
  /** @ignore */
  readOnly?: boolean;
  /** @ignore */
  textOnly?: boolean;
  /** @ignore */
  isValid?: boolean;
  /** @ignore */
  hasWarning?: boolean;
  /** @ignore */
  hasCaution?: boolean;
  /** @ignore */
  hasInfo?: boolean;
  /** @ignore */
  isTouched?: boolean;
  /** @ignore */
  isSelected?: boolean;
  /** Makes the `ToggleButton` width 100% */
  fullWidth?: boolean;
}

interface ToggleButtonStatics {
  StyledLabel: typeof StyledLabel;
}

const ToggleButtonDefaultProps = {
  isValid: true,
  isTouched: false,
  fullWidth: false,
};

/**
 * @mode developer
 */
const ToggleButton: FC<ToggleButtonProps> & ToggleButtonStatics = ({
  children, label, disabled, readOnly, textOnly, name, isSwitch, isChips, isValid, hasWarning, hasCaution, hasInfo, isTouched, isSelected, fullWidth, ...props
}) => {
  const { colId } = useContext(FormColContext);
  return (
    <StyledLabel
      data-test-name={name}
      isSwitch={isSwitch}
      isChips={isChips}
      isValid={isValid}
      hasWarning={hasWarning}
      hasCaution={hasCaution}
      hasInfo={hasInfo}
      isTouched={isTouched}
      fullWidth={fullWidth}
    >
      <StyledHiddenRadioButton
        data-testid="ToggleButton"
        aria-labelledby={colId}
        {...props}
        type="radio"
        name={name}
        disabled={disabled || readOnly || textOnly}
        readOnly={readOnly}
      />
      {!isSwitch && !isChips && <StyledBullet isSelected={isSelected} />}
      <StyledToggleButton
        disabled={disabled}
        readOnly={readOnly}
        isValid={isValid}
        hasWarning={hasWarning}
        hasCaution={hasCaution}
        hasInfo={hasInfo}
        isTouched={isTouched}
        leftPadding={(isSwitch || isChips) ? 11 : 40}
        className={isSelected ? 'iA_Core_ToggleButton--selected' : ''}
        fullWidth={fullWidth}
      >

        {isChips && <StyledCheckIcon as={CheckIcon} />}
        {label || children}

      </StyledToggleButton>
    </StyledLabel>
  );
};

ToggleButton.defaultProps = ToggleButtonDefaultProps;
ToggleButton.StyledLabel = StyledLabel;

export default ToggleButton;
